import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Form,
  Button,
  Space,
  Tooltip,
  Typography,
  Input,
  Card,
  Tabs,
  TabsProps,
} from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Account from "../../../models/Account";
import { NavigationContext } from "../../../context/NavigationContext";
import { ToastContext } from "../../../context/ToastContext";
import AccountService from "../../../services/crud/AccountService";
import DescriptionDataList from "../../../components/DescriptionDataList";
import { useForm } from "antd/lib/form/Form";
import { OverlayContext } from "../../../context/OverlayContext";
import { QuestionCircleFilled } from "@ant-design/icons/lib/icons";
import { EntitySelector } from "../../../components/forms/selects/EntitySelector";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import Hub from "../../../models/Hub";
import { Modes } from "../../../Modes";
import { HubsTable } from "./HubsTable";
import UserGroupService from "../../../services/crud/UserGroupService";
import { UserGroup, UserGroupTableRow } from "../../../models/UserGroup";
import UsersTable from "./UsersTable";
import VitecService from "../../../services/VitecService";
import TextArea from "antd/lib/input/TextArea";
import UserGroupsTable from "../../../components/tables/UserGroupsTable";
import useTabsSyncWithUrl from "../../../hooks/tabsSyncWithUrl";
import { commaSeparatedListToArray, flattenCommaSeparatedList } from "../../../utils/CommaSeparatedLists";
import Checkbox from "antd/es/checkbox";
import UserService from "../../../services/crud/UserService";
import User from "../../../models/User";
import { GenericMobilityContractPayoutConfigsTable } from "../../../components/tables/MobilityContractPayoutConfigTable";

const hubsState = atom<Hub[]>({
  key: "real_estate_hubs",
  default: [],
});

export const accountState = atom<Account>({
  key: "real_estate_account",
  default: {} as Account,
});

const Header = () => {
  let navigate = useNavigate();
  const gotoPage = (route) => {
    console.log("route is: " + route);
    navigate(route);
  };
  return (
    <div>
      <Button size="small" icon={<ArrowLeftOutlined />} type="link" onClick={() => gotoPage(-1)}>
        Back to real estates list
      </Button>
    </div>
  );
};

const ProfileSection = ({ account, actions = <></> }) => {
  const data = {
    ...account,
  };
  return (
    <div>
      <Card>
        <DescriptionDataList data={data} footer={<Space>{actions}</Space>} />
      </Card>
    </div>
  );
};

const EditProfileForm = ({ account, form }) => {
  return (
    <Form form={form}>
      <Form.Item name="id" label="id" required>
        <Input disabled />
      </Form.Item>
      <Form.Item name="name" label="name" required>
        <Input />
      </Form.Item>
      <Tooltip title="Name of your company">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
      <Form.Item name="whiteLabelId" label="White label">
        <EntitySelector.WhiteLabels></EntitySelector.WhiteLabels>
      </Form.Item>
      <Form.Item name="authorizedDomains" label="Authorized email domains">
        <Input />
      </Form.Item>
      <Tooltip title="Separate domains with comma as: @domain1.se, domain2.com">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
      <Form.Item name="defaultAllowBookByInvoice" label="Default book by invoice" valuePropName="checked" rules={[{ type: "boolean" }]}>
        <Checkbox />
      </Form.Item>
      <Tooltip title="Default value for 'allow book by invoice'. Applies to new users or invited users at the time when they are added/invited.">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
      <Form.Item name="tags" label="tag">
        <EntitySelector.Tag></EntitySelector.Tag>
      </Form.Item>
      <Tooltip title="Members of this account will get the listed tags on their Person in customer.io. Don't use punctuation in the tag names. Use lower case.">
        <QuestionCircleFilled></QuestionCircleFilled>
      </Tooltip>
    </Form>
  );
};

const EditJsonConfigForm = ({ accountId, jsonConfig, form }) => {
  return (
    <Form form={form} initialValues={{id: accountId, jsonConfig,}}>
      <Form.Item name="id" label="id" required>
        <Input disabled />
      </Form.Item>
      <Form.Item name="jsonConfig" label="Json Config">
        <TextArea />
      </Form.Item>
    </Form>
  );
};

export type RealeEstateAccountsProps = {};
/**
 * This page lists all company accounts.
 * @param param0
 * @returns
 */
function RealEstateAccountsView({}: RealeEstateAccountsProps) {
  const [form] = useForm();
  const [jsonConfigForm] = useForm();
  const { openModal, closeModal } = useContext(OverlayContext);
  const { mode } = useContext(NavigationContext);
  const [activeKey, onTabChange] = useTabsSyncWithUrl('users');
  const { id } = useParams<any>();
  const accountId = parseInt(id);
  const [account, setAccount] = useRecoilState(accountState);
  const { loading, loadMessage } = useContext(ToastContext);
  const [userGroupTableRows, setUserGroupsTableRows] = useState<(UserGroupTableRow)[]>([]);
  const users = [
    ...(account?.users ?? []),
  ].sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase()));
  const loadAccount = useCallback(async () => {
    if (accountId)
      await Promise.all([
        AccountService.getAccount(accountId).then((account) => {
          setAccount(account);
        }),
        UserGroupService.listUserGroupsOnAccountWithContractsAndMembers(accountId).then((tableRows) => {
          console.log('tableRows', tableRows)
          UserGroup.sortUserGroupTableRowsByLabel(tableRows);
          setUserGroupsTableRows([...tableRows]);
        }),
      ]);
  }, [accountId]);

  useEffect(() => {
    loadMessage("Loading", () =>
      loadAccount()
        .then(() => "Loaded")
        .catch(() => "error loading ")
    );
  }, [accountId]);

  const handleEdit = () => {
    openEditModal();
  };
  const openEditModal = () => {
    form.resetFields();
    form.setFieldsValue({
      ...account,
      tags: commaSeparatedListToArray(account.tags),
    });
    openModal({
      title: "Edit account profile",
      content: <EditProfileForm account={account} form={form} />,
      onOk: () => {
        form.validateFields().then((values) => {
          values['tags'] = flattenCommaSeparatedList(values['tags'] ?? '');
          values['defaultAllowBookByInvoice'] = values['defaultAllowBookByInvoice'] == true; // two equals intentionally to convert 1 to true.
          loadMessage("Adding item...", async () => {
            await AccountService.editAccount(account?.id, values);
            AccountService.getAccount(account?.id).then((account) => {
              setAccount(account);
            });
            return "Updated account profile";
          });
          closeModal();
        });
      },
    });
  };
  const handleEditJsonConfig = () => {
    openEditJsonConfigModal();
  };
  const openEditJsonConfigModal = async () => {
    jsonConfigForm.resetFields();
    let jsonConfig = null;
    await loadMessage('Loading config', async () => {
      jsonConfig = await AccountService.getJsonConfig(accountId);
    });
    openModal({
      title: "Edit account json config",
      content: <EditJsonConfigForm accountId={accountId} jsonConfig={jsonConfig} form={jsonConfigForm} />,
      onOk: () => {
        jsonConfigForm.validateFields().then((values) => {
          loadMessage("Saving config...", async () => {
            await AccountService.updateJsonConfig(values.id, values.jsonConfig);
            return "Updated account config";
          });
          closeModal();
        });
      },
    });
  };
  const handleVitecSync = () => {
    loadMessage('Vitec sync...', async () =>
      await VitecService.sync(accountId).then(() => {
        alert('Sync completed');
      }).catch(() => {
        alert('Sync failed (sometimes multiple syncs are required to get all users)');
      }).finally(() => {
        loadAccount();
      })
    );
  }
  let profileActions = <></>;
  let userTableActions = { updateBalance: false, viewUser: false };
  switch (mode) {
    case Modes.SYSTEM_ADMIN:
      userTableActions = { updateBalance: true, viewUser: true };
      profileActions = (
        <Space style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
          <Space>
            <Button disabled={loading} type="primary" onClick={handleEdit}>
              Edit
            </Button>
          </Space>
          <h2 style={{marginTop: 20}}>User Access Automation</h2>
          <Space>
            <Button disabled={loading} type="primary" onClick={handleEditJsonConfig}>
              Edit Json Config
            </Button>
            <Button disabled={loading} type="primary" onClick={handleVitecSync}>
              Trigger manual Vitec Sync
            </Button>
          </Space>
        </Space>
      );
      break;
    case Modes.COMPANY_ADMIN:
      profileActions = <></>;
      userTableActions = { updateBalance: false, viewUser: false };
      break;
    case Modes.REAL_ESTATE_ADMIN:
      profileActions = <></>;
      userTableActions = { updateBalance: false, viewUser: false };
      break;
  }

  const handleActionsComplete = () => {
    loadAccount();
  };

  const tabItems: TabsProps["items"] = [
    {
      key: "users",
      label: `Users`,
      children: (
        <UsersTable
          dataSource={users ?? []}
          userGroups={userGroupTableRows ?? []}
          accountId={account.id}
          onActionComplete={handleActionsComplete}
          actions={userTableActions}
          loading={loading}
          showUserGroups={true}
        ></UsersTable>
      ),
    },
    {
      key: "apartments",
      label: `Apartments`,
      children: (
//        <AccountsUserGroupsTab accountId={account.id} />

        <UserGroupsTable
          userGroups={userGroupTableRows}
          accountId={account.id}
          accountName={account.name}
          onActionComplete={handleActionsComplete}
        />
      ),
    },
    {
      key: "hubs",
      label: `Hubs`,
      children: <HubsTable id={account?.id} hubsState={hubsState}></HubsTable>,
    },
    {
      key: "mobility",
      label: "Mobilitetspaket",
      children: <div>
          <Typography.Title level={3}>Mobilitet</Typography.Title>
          <GenericMobilityContractPayoutConfigsTable accountId={accountId} />
        </div>
    },
    {
      key: "profile",
      label: `Real Estate overview`,
      children: <ProfileSection actions={profileActions} account={account}></ProfileSection>,
    },
  ];

  return (
    <div>
      <Header></Header>
      <Typography.Title level={1} style={{ marginTop: "15px" }}>
        {account?.name}
      </Typography.Title>
      <Tabs
        type="line"
        style={{ minHeight: "100vh" }}
        defaultActiveKey="users"
        items={tabItems}
        activeKey={activeKey}
        onChange={onTabChange} />
    </div>
  );
}

export default RealEstateAccountsView;
